import { MouseEvent, useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import styled from "styled-components";
import { AdminActionButton } from "../../../Component/UI/AdminPageHeader";
import ProductThumbnail from "../../../Component/UI/ProductThumbnail";
import RemainingStock from "./RemainingStock";
import { getProductSaleStatus, showExposurePrice } from "../../../Component/tool/productExposure";
import ReviewOptionInventory, { isWebInventoryReview } from '../../Broadcast/ReviewOptionInventory';
import PriceVisibilityButton from '../../Broadcast/PriceVisibilityButton';

type Props = {
  product: { seq?: number; name: string; price: number; product_num: number | string | null; exposure_price_mode?: string; product_status?: string; option_check?: string };
  imageUrl: string;
  count: number;
  liveSeq?: number;
  rehearsal?: boolean;
  copyableTitle?: boolean;
  onChangePriceVisibility?: () => Promise<void>;
};

export default function BroadcastProductCard({ product, imageUrl, count, copyableTitle = false, onChangePriceVisibility }: Props) {
  const reviewOptions = product.option_check === 'y' && isWebInventoryReview();
  const [copying, setCopying] = useState(false);
  const [copyNotice, setCopyNotice] = useState<{ id: number; success: boolean } | null>(null);
  const copyRevision = useRef(0);
  const copyButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setCopying(false);
    setCopyNotice(null);
    return () => { copyRevision.current += 1; };
  }, [product.seq, product.name, copyableTitle]);

  useEffect(() => {
    if (!copyNotice) return;
    const timer = window.setTimeout(() => setCopyNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [copyNotice]);

  const copyTitle = async (event: MouseEvent<HTMLButtonElement>) => {
    const revision = ++copyRevision.current;
    // The preview can move into a separate window; its focused document owns clipboard access.
    const clipboard = event.currentTarget.ownerDocument.defaultView?.navigator.clipboard;
    setCopying(true);
    setCopyNotice(null);
    try {
      if (!clipboard?.writeText) throw new Error("Clipboard unavailable");
      await clipboard.writeText(product.name);
      if (copyRevision.current === revision) setCopyNotice({ id: revision, success: true });
    } catch {
      if (copyRevision.current === revision) setCopyNotice({ id: revision, success: false });
    } finally {
      if (copyRevision.current === revision) setCopying(false);
    }
  };

  return <Card $copyable={copyableTitle} $reviewOptions={reviewOptions}>
    <ExposureStatus $soldOut={getProductSaleStatus(product.product_status) === '품절'} $visible={showExposurePrice(product.exposure_price_mode)}>
      <span>{getProductSaleStatus(product.product_status) === '품절' ? '품절' : showExposurePrice(product.exposure_price_mode) ? '가격 공개' : '가격 비공개'}</span>
      {onChangePriceVisibility && <PriceVisibilityButton key={product.seq} publicPrice={showExposurePrice(product.exposure_price_mode)} onChange={onChangePriceVisibility} />}
    </ExposureStatus>
    <Thumbnail src={imageUrl} alt={product.name} />
    <Details $copyable={copyableTitle}>
      <Name title={product.name}>{product.name}</Name>
      {copyableTitle && <CopyTitleButton ref={copyButton} type="button" aria-label={`${product.name} 상품명 복사`} title="상품명 복사"
        disabled={copying} aria-busy={copying} onClick={event => void copyTitle(event)}>
        <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
          <path d="M8 7V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M5 8h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" />
        </svg>
      </CopyTitleButton>}
    </Details>
    <RemainingStock count={count} price={product.price} />
    {reviewOptions && <ReviewOptionInventory productSeq={product.seq} />}
    {copyableTitle && copyNotice && createPortal(<CopyNotice key={copyNotice.id} role={copyNotice.success ? 'status' : 'alert'}>
      {copyNotice.success ? '복사 완료' : '복사하지 못했습니다. 다시 시도해주세요.'}
    </CopyNotice>, copyButton.current?.ownerDocument.body || document.body)}
  </Card>;
}

const Card = styled.div<{ $copyable: boolean; $reviewOptions: boolean }>`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: 124px;
  flex: none;
  align-self: flex-start;
  margin-top: 20px;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  grid-template-rows: minmax(24px, auto) 64px minmax(36px, auto);
  border-radius: 10px;
  overflow: hidden;
  background: var(--admin-theme-surface, var(--user-surface, #fff));
  ${({ $copyable }) => $copyable && `
    height: auto;
    grid-template-rows: minmax(24px, auto) auto minmax(36px, auto);
  `}
  ${({ $reviewOptions }) => $reviewOptions && `height: auto; grid-template-rows: minmax(24px, auto) 64px minmax(36px, auto) auto;`}
  .broadcast-product-metrics { padding-block: 8px; }
  .broadcast-product-metrics strong { font-size: 18px; font-weight: 600; }
  > [role="status"] {
    grid-column: 1 / -1;
    min-width: 0;
    flex-direction: row;
    justify-content: space-between;
    padding: 4px 12px;
    gap: 8px;
    strong { font-size: 22px; }
  }
`;
const ExposureStatus = styled.div<{ $visible: boolean; $soldOut: boolean }>`
  grid-column: 1 / -1; display: flex; align-items: center; gap: 8px; padding: 0 var(--space-3);
  background: var(--color-bg-elevated); color: ${({ $visible, $soldOut }) => $soldOut ? 'var(--color-danger)' : $visible ? 'var(--color-success)' : '#ff617a'}; font-size: var(--text-caption); font-weight: 600;
`;
const Thumbnail = styled(ProductThumbnail)`
  display: block;
  width: 64px;
  height: 64px;
`;
const Details = styled.div<{ $copyable: boolean }>`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  padding: 4px 10px;
  box-sizing: border-box;
  min-height: 0;
  color: var(--admin-theme-text, var(--user-text, #242129));
  ${({ $copyable }) => $copyable && `
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-content: center;
    column-gap: var(--space-1);
    > div { grid-column: 1; align-self: center; }
    > div:nth-of-type(2) { grid-column: 1 / -1; }
  `}
`;
const CopyTitleButton = styled(AdminActionButton)`
  grid-column: 2;
  grid-row: 1;
  align-self: center;
  && { width: var(--touch-target); min-width: var(--touch-target); min-height: var(--touch-target); padding: 0; }
  flex: none;
  color: var(--color-text-link);
  svg {
    display: block;
    width: var(--space-5);
    height: var(--space-5);
    min-width: var(--space-5);
    flex: 0 0 var(--space-5);
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;
const CopyNotice = styled.div`
  position: fixed; z-index: var(--z-toast); left: 50%; bottom: calc(var(--space-6) + env(safe-area-inset-bottom));
  transform: translateX(-50%); box-sizing: border-box; width: max-content; max-width: calc(100vw - var(--space-8));
  padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); background: var(--color-bg-inverse);
  color: var(--color-text-on-inverse); font-family: var(--font-family-base); font-size: var(--text-body); line-height: var(--leading-body);
  &::after { content: ''; position: absolute; left: 8px; right: 8px; bottom: 4px; height: 3px; background: currentColor; opacity: .5; transform-origin: left; animation: product-copy-countdown 3s linear forwards; }
  @keyframes product-copy-countdown { from { transform: scaleX(1); } to { transform: scaleX(0); } }
`;
const Name = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
`;
